import { addDays, startOfDay, startOfWeek } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DateTimeInput, SelectInput, required } from 'react-admin';

export type DatePreset = {
  id: string;
  name: string;
  filter: () => {
    createdAtGte?: Date | null;
    createdAtLte?: Date | null;
  };
};

export const datePresets: DatePreset[] = [
  {
    id: 'today',
    name: 'Today',
    filter: () => ({
      createdAtGte: startOfDay(new Date()),
    }),
  },
  {
    id: 'yesterday',
    name: 'Yesterday',
    filter: () => {
      const yesterday = startOfDay(addDays(new Date(), -1));

      return {
        createdAtGte: yesterday,
        createdAtLte: addDays(yesterday, 1),
      };
    },
  },
  {
    id: 'currentWeek',
    name: 'Current Week',
    filter: () => ({
      createdAtGte: startOfWeek(new Date(), { weekStartsOn: 1 }),
    }),
  },
  {
    id: 'last7days',
    name: 'Last 7 Days',
    filter: () => ({
      createdAtGte: addDays(new Date(), -7),
    }),
  },
  {
    id: 'lastWeek',
    name: 'Previous Week',
    filter: () => {
      const sevenDaysAgo = addDays(new Date(), -7);
      const lastWeek = startOfWeek(sevenDaysAgo, { weekStartsOn: 0 });

      return {
        createdAtGte: lastWeek,
        createdAtLte: addDays(lastWeek, 7),
      };
    },
  },
  {
    id: 'last30days',
    name: 'Last 30 Days',
    filter: () => ({
      createdAtGte: addDays(new Date(), -30),
    }),
  },
  {
    id: 'custom',
    name: 'Custom',
    filter: () => ({}),
  },
];

/** Presets that imply a bounded range (excludes `custom`). */
export const datePresetsMap = new Map(
  datePresets
    .filter(p => p.id !== 'custom')
    .map(preset => [preset.id, preset]),
);

const hasRangeValue = (v: unknown) =>
  v != null &&
  v !== '' &&
  !(typeof v === 'string' && v.trim() === '');

/**
 * Escape hatch for external callers (e.g. a list-context controller that
 * broadens filters when an admin searches by id/name/email). When a field is
 * registered here, the next "preset -> custom" transition for that field
 * will NOT auto-seed gte/lte from the previous preset. The flag is consumed
 * exactly once.
 */
const fieldsToSkipNextSeed = new Set<string>();

export const suppressNextPresetSeed = (fieldName: string) => {
  fieldsToSkipNextSeed.add(fieldName);
};

const PresetSelectWithSeeding = (
  props: {
    fieldName: string;
    datePresetPinned: boolean;
    /** Declared on the wrapper so RA FilterForm getShownFilters keeps this row mounted. */
    source: string;
    /** Set on the wrapper so FilterFormInput hides the remove control (must not be only on nested SelectInput). */
    alwaysOn?: boolean;
  },
) => {
  const { fieldName, datePresetPinned, source } = props;
  const { setValue, getValues } = useFormContext();
  const preset = useWatch({ name: source });
  const prevPresetRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const gteKey = `${fieldName}Gte` as const;
    const lteKey = `${fieldName}Lte` as const;

    if (
      preset === 'custom' &&
      prevPresetRef.current !== undefined &&
      prevPresetRef.current !== 'custom'
    ) {
      if (fieldsToSkipNextSeed.has(fieldName)) {
        fieldsToSkipNextSeed.delete(fieldName);
        prevPresetRef.current = preset;
        return;
      }

      const gte = getValues(gteKey);
      const lte = getValues(lteKey);

      if (hasRangeValue(gte) && hasRangeValue(lte)) {
        prevPresetRef.current = preset;
        // #region agent log
        fetch(
          'http://127.0.0.1:7884/ingest/2159d7f8-bd80-4905-a92c-4ac824bac7b6',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Debug-Session-Id': 'c4e98f',
            },
            body: JSON.stringify({
              sessionId: 'c4e98f',
              hypothesisId: 'H-seed',
              location: 'DateFilters.tsx:PresetSelectWithSeeding',
              message: 'skip seed both range inputs set',
              data: {
                fieldName,
                preset,
                prev: prevPresetRef.current,
              },
              timestamp: Date.now(),
            }),
          },
        ).catch(() => {});
        // #endregion
        return;
      }

      const prevId = prevPresetRef.current;
      if (prevId && prevId !== 'custom') {
        const p = datePresetsMap.get(prevId);
        if (p) {
          const r = p.filter();
          setValue(gteKey, r.createdAtGte ?? null, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue(lteKey, r.createdAtLte ?? null, {
            shouldDirty: true,
            shouldValidate: true,
          });
          // #region agent log
          fetch(
            'http://127.0.0.1:7884/ingest/2159d7f8-bd80-4905-a92c-4ac824bac7b6',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Debug-Session-Id': 'c4e98f',
              },
              body: JSON.stringify({
                sessionId: 'c4e98f',
                hypothesisId: 'H-seed',
                location: 'DateFilters.tsx:PresetSelectWithSeeding',
                message: 'seed from prev preset',
                data: {
                  fieldName,
                  preset,
                  prevId,
                  seededGte: !!r.createdAtGte,
                  seededLte: !!r.createdAtLte,
                },
                timestamp: Date.now(),
              }),
            },
          ).catch(() => {});
          // #endregion
        }
      }
    }

    prevPresetRef.current = preset ?? undefined;
  }, [preset, fieldName, setValue, getValues]);

  return (
    <SelectInput
      label="Date Preset"
      source={source}
      alwaysOn={datePresetPinned}
      choices={datePresets}
      {...(datePresetPinned
        ? {
            validate: [required()],
            resettable: false,
          }
        : { emptyText: 'All' })}
    />
  );
};

/** RA FilterForm only mounts rows with alwaysOn, displayedFilters, or a set value — range fields stay mounted and hide via sx when collapsed. Top-level `alwaysOn` hides the remove control in FilterFormInput. */
const CollapsedRangeDateTimeInput = (
  props: {
    fieldName: string;
    label?: string;
    suffix: 'Gte' | 'Lte';
    alwaysOn?: boolean;
  },
) => {
  const { fieldName, label, suffix } = props;
  const preset = useWatch({ name: `${fieldName}DatePreset` });
  const source = `${fieldName}${suffix}`;
  const visible = preset === 'custom';
  const prevVisibleRef = useRef(visible);

  useEffect(() => {
    if (prevVisibleRef.current === visible) {
      return;
    }
    prevVisibleRef.current = visible;
    // #region agent log
    fetch(
      'http://127.0.0.1:7884/ingest/2159d7f8-bd80-4905-a92c-4ac824bac7b6',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Debug-Session-Id': 'c4e98f',
        },
        body: JSON.stringify({
          sessionId: 'c4e98f',
          hypothesisId: 'H-RA-filter-row',
          location: 'DateFilters.tsx:CollapsedRangeDateTimeInput',
          message: 'range visibility changed',
          data: { source, preset, visible },
          timestamp: Date.now(),
        }),
      },
    ).catch(() => {});
    // #endregion
  }, [visible, preset, source]);

  const rangeFieldLabel =
    suffix === 'Gte'
      ? label
        ? `${label} (from)`
        : 'From date'
      : label
        ? `${label} (to)`
        : 'To date';

  return (
    <DateTimeInput
      source={source}
      label={rangeFieldLabel}
      alwaysOn
      sx={{ display: visible ? undefined : 'none' }}
    />
  );
};

export type CreateDateFiltersOptions = {
  /**
   * When true: preset stays in the filter form (alwaysOn, no remove row control)
   * and cannot be cleared to an unbounded range (no empty choice, no clear icon).
   */
  datePresetPinned?: boolean;
  /**
   * When true: show After/Before only when preset is `custom`; preset row appears first.
   */
  collapseDateRangeUnlessCustom?: boolean;
};

export const createDateFilters = (
  fieldName: string,
  label?: string,
  options?: CreateDateFiltersOptions,
) => {
  const datePresetPinned = options?.datePresetPinned ?? false;
  const collapseDateRangeUnlessCustom =
    options?.collapseDateRangeUnlessCustom ?? false;

  const presetSource = `${fieldName}DatePreset`;

  const presetSelect = (
    <PresetSelectWithSeeding
      key={`${fieldName}Preset`}
      alwaysOn={datePresetPinned}
      fieldName={fieldName}
      datePresetPinned={datePresetPinned}
      source={presetSource}
    />
  );

  if (collapseDateRangeUnlessCustom) {
    return [
      presetSelect,
      <CollapsedRangeDateTimeInput
        key={`${fieldName}Gte`}
        alwaysOn
        fieldName={fieldName}
        label={label}
        suffix="Gte"
      />,
      <CollapsedRangeDateTimeInput
        key={`${fieldName}Lte`}
        alwaysOn
        fieldName={fieldName}
        label={label}
        suffix="Lte"
      />,
    ];
  }

  return [
    <DateTimeInput
      key={`${fieldName}Gte`}
      source={`${fieldName}Gte`}
      label={label ? `${label} (from)` : 'From date'}
    />,
    <DateTimeInput
      key={`${fieldName}Lte`}
      source={`${fieldName}Lte`}
      label={label ? `${label} (to)` : 'To date'}
    />,
    presetSelect,
  ];
};
